import SyntaxHighlighter from 'react-syntax-highlighter';
import React, { FC } from 'react';
import { CodeBoxContainer, CodeBoxCopyIcon } from './CodeBox.styles';
import { Tooltip } from '../Tooltip';
import { nanoid } from 'nanoid';
import { useTimeoutToggle } from '../../hooks/useTimeoutToggle';

type CodeBoxProps = {
  disabled?: boolean;
  disabledTooltip?: string;
  showLineNumber?: boolean;
  // Available language options (add if needed): https://github.com/react-syntax-highlighter/react-syntax-highlighter/blob/HEAD/AVAILABLE_LANGUAGES_HLJS.MD
  language: 'sql' | 'javascript' | 'html' | 'json';
  children: string;
  onCopy?: F0;
};

export const CodeBox: FC<CodeBoxProps> = ({
  disabled,
  disabledTooltip,
  showLineNumber = false,
  language,
  children,
  onCopy,
}) => {
  const [copied, toggleCopied] = useTimeoutToggle(2000);

  const tooltipCopyId = `copyCodeBoxButton-${nanoid()}`;
  const tooltipDisabledId = `disabledCodeBox-${nanoid()}`;

  const handleCopy = () => {
    navigator.clipboard
      .writeText(children)
      .then(() => {
        toggleCopied();
        onCopy?.();
      })
      .catch(() => window.alert('Oops, unable to copy'));
  };

  return (
    <>
      <CodeBoxContainer disabled={disabled} data-tooltip-id={tooltipDisabledId}>
        <SyntaxHighlighter
          language={language}
          wrapLongLines
          showLineNumbers={showLineNumber}
          lineProps={{ style: { flexWrap: 'wrap' } }}
        >
          {children}
        </SyntaxHighlighter>
        {!disabled && (
          <CodeBoxCopyIcon
            onClick={handleCopy}
            data-tooltip-id={tooltipCopyId}
          />
        )}
      </CodeBoxContainer>
      <Tooltip
        id={tooltipCopyId}
        place="left"
        variant="success"
        content="Copied!"
        isOpen={copied}
      />
      {disabled && disabledTooltip && (
        <Tooltip isMultiline id={tooltipDisabledId} float>
          {disabledTooltip}
        </Tooltip>
      )}
    </>
  );
};
